/**
 * API Client Utility
 * 
 * TEMPORARY NOTE: This utility includes support for self-signed certificates
 * due to Let's Encrypt rate limiting issues. This is a temporary workaround
 * until the rate limiting period expires and proper certificates are obtained.
 * 
 * TODO: Remove self-signed certificate support once Let's Encrypt rate limiting
 * is resolved and proper certificates are in place.
 */

interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  headers?: Record<string, string>
  body?: any
  credentials?: RequestCredentials
}

import { API_URL } from '../config'

// Global logout function to be set by the AuthProvider
let globalLogout: (() => void) | null = null

export const setGlobalLogout = (logoutFn: () => void) => {
  globalLogout = logoutFn
}

const redirectToLoginWithAuthExpired = () => {
  if (globalLogout) {
    globalLogout()
  }
  const authExpiredMessage = encodeURIComponent('Authentication expired. Please sign in again.')
  window.location.href = `/login?error=auth_expired&error_description=${authExpiredMessage}`
}

const isAuthExpiredError = (status: number, errorData: any, requestUrl?: string): boolean => {
  if (status === 401) return true
  if (status !== 403) return false

  const errorText = String(errorData?.error || '').toLowerCase()

  // Keep known business-logic 403 errors as normal in-app errors.
  if (errorText.includes('repository limit reached')) return false

  // Treat explicit auth-expired signals as expired sessions.
  if (errorData?.requires_reauth) return true
  if (errorData?.type === 'auth_expired') return true

  return errorText.includes('authentication') && errorText.includes('expired')
    // Repository save 403 should behave like auth expiration per onboarding UX.
    || Boolean(requestUrl?.includes('/api/repositories'))
}

// Helper function to handle 401 responses for direct fetch calls
export const handleFetchResponse = async (response: Response): Promise<Response> => {
  if (response.status === 401 || response.status === 403) {
    const errorData = await response.clone().json().catch(() => ({ error: 'Unknown error' }))
    
    // Check if this is a GitHub authentication error that requires re-authentication
    if (errorData.requires_reauth && errorData.re_auth_url) {
      console.warn('GitHub re-authentication required, redirecting to GitHub')
      // Redirect to GitHub for re-authentication
      window.location.href = errorData.re_auth_url
      throw new Error('GitHub re-authentication required')
    }

    if (isAuthExpiredError(response.status, errorData, response.url)) {
      console.warn(`${response.status} auth-expired response received, redirecting to login`)
      redirectToLoginWithAuthExpired()
      throw new Error('Authentication expired - please log in again')
    }
  }
  return response
}

class ApiClient {
  private baseUrl: string

  constructor() {
    this.baseUrl = API_URL
  }

  /**
   * Makes an API request with support for self-signed certificates
   * TEMPORARY: This includes workarounds for Let's Encrypt rate limiting
   *
   * Automatically injects Authorization header from localStorage if available
   */
  async request<T = any>(endpoint: string, options: ApiClientOptions = {}): Promise<T> {
    const {
      method = 'GET',
      headers = {},
      body,
      credentials = 'include'
    } = options

    const url = `${this.baseUrl}${endpoint}`

    // Automatically inject Authorization header from localStorage if not already provided
    const token = localStorage.getItem('github_access_token')
    const authHeaders: Record<string, string> = {}
    if (token && !headers['Authorization']) {
      authHeaders['Authorization'] = `Bearer ${token}`
    }

    const requestOptions: RequestInit = {
      method,
      credentials,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders,
        ...headers,
      },
    }

    if (body) {
      requestOptions.body = JSON.stringify(body)
    }

    try {
      const response = await fetch(url, requestOptions)
      
      if (!response.ok) {
        // Handle auth failures (401 + explicit auth-expired 403)
        if (response.status === 401) {
          const errorData = await response.clone().json().catch(() => ({ error: 'Unknown error' }))
          
          // Check if this is a GitHub authentication error that requires re-authentication
          if (errorData.requires_reauth && errorData.re_auth_url) {
            console.warn('GitHub re-authentication required, redirecting to GitHub')
            // Redirect to GitHub for re-authentication
            window.location.href = errorData.re_auth_url
            throw new Error('GitHub re-authentication required')
          }

          if (isAuthExpiredError(response.status, errorData, url)) {
            console.warn(`${response.status} auth-expired response received, redirecting to login`)
            redirectToLoginWithAuthExpired()
            throw new Error('Authentication expired - please log in again')
          }
        }
        
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`)
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        return await response.json()
      }
      
      return await response.text() as T
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error)
      throw error
    }
  }

  // Convenience methods
  async get<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', headers })
  }

  async post<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, headers })
  }

  async put<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, headers })
  }

  async delete<T = any>(endpoint: string, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', headers })
  }

  async patch<T = any>(endpoint: string, body?: any, headers?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'PATCH', body, headers })
  }
}

// Export singleton instance
export const apiClient = new ApiClient()

// Export the class for testing or custom instances
export { ApiClient }
